import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryResult, useApolloClient } from 'react-apollo';
import { StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';
import * as Sentry from '@sentry/react';

import { UpdateUserProfileDocument } from 'api';
import { getFullName, getRandomProfileImage, getSubdomainUrl } from 'helpers';
import storage from 'storage';
import { isRoot } from 'utilities';
import { GETSTREAM_API_KEY, IMGIX_HOST, INTERCOM_APP_ID } from 'settings';
import {
  ClientContext,
  FeaturesProvider,
  FollowedProvider,
  RefetcherProvider,
  UserContext,
  SocialConnectionsProvider,
} from 'providers';

interface IProps {
  queryResult: QueryResult;
  children: ReactNode;
}

const Providers: FC<IProps> = ({ queryResult, children }) => {
  const apolloClient = useApolloClient();
  const { i18n } = useTranslation();
  const [chatClient, setChatClient] = useState(null);

  const { bootstrap } = queryResult.data;
  const { client, user } = bootstrap;

  // Triggered after an SSO user signs in
  // IMPORTANT: Prevents rendering the app on the base domain
  if (isRoot()) {
    window.location.assign(getSubdomainUrl(client.subdomain));
    return null;
  }

  async function reconcileLanguage() {
    const { languages } = i18n;
    const isLanguageMatch = user.lang === languages[0];

    if (isLanguageMatch) {
      return;
    }

    await apolloClient.mutate({
      mutation: UpdateUserProfileDocument,
      variables: { data: { id: user.id, lang: languages[0] } },
    });
  }

  function setupIntercom() {
    window.Intercom?.('boot', {
      app_id: INTERCOM_APP_ID,
      alignment: 'left',
      email: user.email_address,
      user_id: user.id,
      client_id: user.client_id,
      client_name: client.title,
      name: getFullName(user),
      job_title: user.job_title,
      department_title: user.department_title,
      location_title: user.location_title,
      created_at: user.created_at,
      german_privacy: user.german_privacy,
      timezone: user.user_timezone,
      user_type: user.user_type,
    });
  }

  function setupSegment() {
    window?.analytics?.identify(user.id, { name: user.full_name, email: user.email_address });
  }

  function setupSentry() {
    Sentry.setUser({
      id: user.id,
      email: user.email_address,
      client: client.title,
      name: user.full_name,
    });
  }

  async function setupStreamChat() {
    const imageUrl = user.image
      ? `${IMGIX_HOST}/${user.image.key}?auto=format,compress`
      : getRandomProfileImage().src;

    const streamChatClient = new StreamChat(GETSTREAM_API_KEY);
    const streamChatUser = { id: user.id, name: user.full_name, image: imageUrl };

    await streamChatClient.connectUser(streamChatUser, user.getstream_token);

    setChatClient(streamChatClient);
  }

  useEffect(() => {
    setupIntercom();
    setupSegment();
    setupSentry();
    setupStreamChat();
    reconcileLanguage();
    storage.clientSubdomain = client.subdomain;
  }, []);

  return (
    <ClientContext.Provider value={{ client }}>
      <UserContext.Provider value={{ user }}>
        <FeaturesProvider>
          <SocialConnectionsProvider>
            <FollowedProvider>
              <RefetcherProvider>
                <Chat client={chatClient}>{children}</Chat>
              </RefetcherProvider>
            </FollowedProvider>
          </SocialConnectionsProvider>
        </FeaturesProvider>
      </UserContext.Provider>
    </ClientContext.Provider>
  );
};

export default Providers;
